import React from "react";
import {
  Box,
  Button,
  Circle,
  Container,
  Flex,
  Heading,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import { ChevronLeftIcon } from "@chakra-ui/icons";
import { FaHome } from "react-icons/fa";
import { TiWavesOutline } from "react-icons/ti";
import { useNavigate } from "react-router-dom";

const TOP_BAR_BLUE = "#00005f";
const DIVIDER_GRAY = "#7a7a7a";

function NavIcons({ firstLetter, onLogout, size = "md" }) {
  const navigate = useNavigate();
  return (
    <HStack spacing={1}>
      <Tooltip label="My Pools" openDelay={300} hasArrow>
        <IconButton
          aria-label="My Pools"
          icon={<TiWavesOutline />}
          variant="ghost"
          color="white"
          fontSize="20px"
          size={size}
          onClick={() => navigate("/my-pools")}
          _hover={{ bg: "whiteAlpha.200" }}
          _active={{ bg: "whiteAlpha.300" }}
        />
      </Tooltip>
      <Tooltip label="Home" openDelay={300} hasArrow>
        <IconButton
          aria-label="Home"
          icon={<FaHome />}
          variant="ghost"
          color="white"
          fontSize="20px"
          size={size}
          onClick={() => navigate("/")}
          _hover={{ bg: "whiteAlpha.200" }}
          _active={{ bg: "whiteAlpha.300" }}
        />
      </Tooltip>
      <Menu>
        <MenuButton
          as={Button}
          variant="ghost"
          color="white"
          _hover={{ bg: "whiteAlpha.200" }}
          _active={{ bg: "whiteAlpha.300" }}
          px={2}
          size={size}
        >
          <HStack spacing={2}>
            <Circle
              size={size === "sm" ? "28px" : "40px"}
              bg="orange.400"
              color="white"
              fontWeight="bold"
              fontSize={size === "sm" ? "sm" : "md"}
            >
              {firstLetter}
            </Circle>
            {size !== "sm" && <Text fontSize="sm">▼</Text>}
          </HStack>
        </MenuButton>
        <MenuList color="black">
          <MenuItem onClick={() => navigate("/profile")}>Update Profile</MenuItem>
          <MenuItem onClick={() => navigate("/change-password")}>Change Password</MenuItem>
          <MenuDivider />
          <MenuItem onClick={onLogout}>Logout</MenuItem>
        </MenuList>
      </Menu>
    </HStack>
  );
}

/**
 * Shared page header.
 *
 * Desktop: single row — [back?] [title] [extra?] [nav icons]
 * Mobile:  two rows  — [title centered] / [back? ··· nav icons]
 *
 * Props:
 *   title    string    — page title (displayed in Orbitron font)
 *   onBack   fn|null   — if provided, renders a back/chevron button
 *   user     object    — auth user (uses user.username for avatar letter)
 *   onLogout fn        — called when user clicks Logout
 *   extra    ReactNode — optional extra element shown between title and nav on desktop
 */
export default function PageHeader({ title, onBack, user, onLogout, extra, mobileSingleRow = false }) {
  // Falls back to email before "?", matching how names are resolved elsewhere.
  // A "?" now means the page forgot to pass `user`, not that the account is odd.
  const firstLetter = (user?.username || user?.email || "?").charAt(0).toUpperCase();

  return (
    <Box bg={TOP_BAR_BLUE} borderBottom={`2px solid ${DIVIDER_GRAY}`} boxShadow="md">
      <Container maxW="container.xl">

        {/* ── Desktop: single row ── */}
        <Flex display={{ base: "none", md: "flex" }} align="center" justify="space-between" py={5}>
          <HStack spacing={2} align="center">
            {onBack && (
              <IconButton
                aria-label="Back"
                icon={<ChevronLeftIcon boxSize={8} />}
                variant="ghost"
                color="white"
                size="lg"
                onClick={onBack}
                _hover={{ bg: "whiteAlpha.200" }}
                _active={{ bg: "whiteAlpha.300" }}
              />
            )}
            <Heading
              fontFamily="'Orbitron', sans-serif"
              fontSize="40px"
              letterSpacing="3px"
              fontWeight="600"
            >
              {title}
            </Heading>
          </HStack>
          <HStack spacing={1}>
            {extra}
            <NavIcons firstLetter={firstLetter} onLogout={onLogout} />
          </HStack>
        </Flex>

        {/* ── Mobile: single row (home page) ── */}
        {mobileSingleRow && (
          <Flex display={{ base: "flex", md: "none" }} align="center" justify="space-between" py={3} px={2}>
            <Heading
              fontFamily="'Orbitron', sans-serif"
              fontSize="18px"
              letterSpacing="2px"
              fontWeight="600"
              noOfLines={1}
            >
              {title}
            </Heading>
            <NavIcons firstLetter={firstLetter} onLogout={onLogout} size="sm" />
          </Flex>
        )}

        {/* ── Mobile: two rows (all other pages) ── */}
        {!mobileSingleRow && (
          <Box display={{ base: "block", md: "none" }}>
            {/* Row 1: back arrow (if any) + title right-aligned */}
            <Flex align="center" pt={3} pb={1} px={2} gap={1}>
              {onBack && (
                <IconButton
                  aria-label="Back"
                  icon={<ChevronLeftIcon boxSize={5} />}
                  variant="ghost"
                  color="white"
                  size="sm"
                  flexShrink={0}
                  onClick={onBack}
                  _hover={{ bg: "whiteAlpha.200" }}
                  _active={{ bg: "whiteAlpha.300" }}
                />
              )}
              <Heading
                fontFamily="'Orbitron', sans-serif"
                fontSize="18px"
                letterSpacing="2px"
                fontWeight="600"
                noOfLines={1}
                flex="1"
                textAlign={onBack ? "right" : "center"}
              >
                {title}
              </Heading>
            </Flex>
            {/* Row 2: nav icons right-aligned */}
            <Flex align="center" justify="flex-end" pb={2} px={1}>
              <NavIcons firstLetter={firstLetter} onLogout={onLogout} size="sm" />
            </Flex>
          </Box>
        )}

      </Container>
    </Box>
  );
}
